import {
  AlignmentType,
  BorderStyle,
  CharacterSet,
  convertInchesToTwip,
  Document,
  ImageRun,
  Packer,
  PageOrientation,
  Paragraph,
  TabStopPosition,
  TabStopType,
  TextRun,
} from "docx";
import {
  formatDate,
  formatProjectPositionHeading,
  sortProjectsByDate,
} from "../components/utils/helpers";
import CVData from "../types/CVData";
import type { SkillItem } from "../types/Skills";

/** OOXML `w:sz` is in half-points; `PT(n)` = n points expressed as half-points (12 → 24). */
const FONT = "Calibri";
const PT = (pt: number) => Math.round(pt * 2);

/**
 * PDF body is 12pt (`cvDocStyles.text`). Word often looks a touch smaller at the same
 * nominal size (viewer zoom, page width, hinting). Slightly raise body text so it matches
 * the PDF more closely when compared at 100% zoom.
 */
const BODY_PT = 12.5;

/** A4 portrait (twips), same as `@react-pdf/renderer` `Page size="A4"`. */
const PAGE_SIZE_A4 = {
  width: 11906,
  height: 16838,
  orientation: PageOrientation.PORTRAIT,
} as const;

const DOC_STYLES = {
  default: {
    document: {
      run: {
        font: FONT,
        size: PT(BODY_PT),
      },
    },
    listParagraph: {
      run: {
        font: FONT,
        size: PT(BODY_PT),
      },
    },
  },
} as const;

/** PDF `styles.line`: thin black rule under section headings. */
const SECTION_TITLE_BORDER = {
  bottom: {
    style: BorderStyle.SINGLE,
    size: 4,
    color: "000000",
    space: 1,
  },
} as const;

const SECTION_TITLE_SPACING = { before: 360, after: 240 } as const;

/** PDF header ~100 pt-wide logo; `ImageRun` uses px. */
const LOGO_PX_W = 100;
const LOGO_PX_H = Math.round((66 / 247) * LOGO_PX_W);

/** PDF page padding: 48 pt H, 80 pt V → twips (pt × 20). */
const PAGE_MARGIN = {
  top: 80 * 20,
  right: 48 * 20,
  bottom: 80 * 20,
  left: 48 * 20,
};

function publicAssetUrl(relativeFile: string): string {
  const base = (process.env.PUBLIC_URL ?? "").replace(/\/$/, "");
  const path = `${base}/${relativeFile}`;
  if (path.startsWith("http") || path.startsWith("//")) return path;
  if (path.startsWith("/")) return path;
  return `/${path}`;
}

function toFontBuffer(ab: ArrayBuffer): Buffer {
  return typeof Buffer !== "undefined"
    ? Buffer.from(ab)
    : (new Uint8Array(ab) as unknown as Buffer);
}

/**
 * Same file as PDF (`Font.register` in CVDocument). Embedding it in the .docx
 * makes normal-weight text match the PDF; bold/italic still rely on Word’s
 * linked faces (the `docx` API only attaches one embed slot per font entry).
 */
async function loadCalibriTtfForEmbed(): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(publicAssetUrl("calibri.ttf"));
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

async function loadSynergiLogoPng(): Promise<ArrayBuffer | null> {
  try {
    const res = await fetch(publicAssetUrl("synergi-logo.png"));
    if (!res.ok) return null;
    return await res.arrayBuffer();
  } catch {
    return null;
  }
}

function appendFormattedWord(
  runs: TextRun[],
  word: string,
  firstPt: number,
  restPt: number,
  bold = true
) {
  const subWords = word.split("-");
  for (let si = 0; si < subWords.length; si++) {
    const sub = subWords[si];
    if (!sub) continue;
    runs.push(
      new TextRun({
        text: sub.charAt(0),
        font: FONT,
        bold,
        size: PT(firstPt),
      })
    );
    if (sub.length > 1) {
      runs.push(
        new TextRun({
          text: sub.slice(1),
          font: FONT,
          bold,
          size: PT(restPt),
        })
      );
    }
    if (si < subWords.length - 1) {
      runs.push(
        new TextRun({ text: "-", font: FONT, bold, size: PT(restPt) })
      );
    }
  }
}

/** Mirrors PDF `formatField`: first letter(s) larger per word (hyphen-aware). */
function formatFieldRuns(
  input: string,
  firstPt: number,
  restPt: number,
  bold = true
): TextRun[] {
  const runs: TextRun[] = [];
  const words = input.split(/\s+/).filter(Boolean);
  words.forEach((word, wi) => {
    appendFormattedWord(runs, word, firstPt, restPt, bold);
    if (wi < words.length - 1) {
      runs.push(
        new TextRun({ text: " ", font: FONT, bold, size: PT(restPt) })
      );
    }
  });
  return runs;
}

function addSectionTitle(out: Paragraph[], titleUpper: string) {
  out.push(
    new Paragraph({
      children: formatFieldRuns(titleUpper, 14, 11),
      border: SECTION_TITLE_BORDER,
      spacing: SECTION_TITLE_SPACING,
    })
  );
}

function skillNames(
  items: SkillItem[] | undefined,
  joinSeparator: string
): string {
  if (!items?.length) return "";
  return items
    .map((s) => (typeof s === "string" ? s : s.name))
    .join(joinSeparator);
}

function pushSkillLine(
  out: Paragraph[],
  label: string,
  items: SkillItem[] | undefined,
  joinSeparator = ", "
) {
  const text = skillNames(items, joinSeparator);
  if (!text) return;
  out.push(
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      children: [
        new TextRun({
          text: `${label}: `,
          font: FONT,
          bold: true,
          size: PT(BODY_PT),
        }),
        new TextRun({ text, font: FONT, size: PT(BODY_PT) }),
      ],
      spacing: { after: 80 },
    })
  );
}

export async function buildCvDocxBlob(
  formData: CVData,
  includeName: boolean
): Promise<Blob> {
  const children: Paragraph[] = [];

  const [logoData, calibriTtf] = await Promise.all([
    loadSynergiLogoPng(),
    loadCalibriTtfForEmbed(),
  ]);
  if (logoData) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new ImageRun({
            type: "png",
            data: logoData,
            transformation: {
              width: LOGO_PX_W,
              height: LOGO_PX_H,
            },
          }),
        ],
        spacing: { after: 200 },
      })
    );
  }

  if (!includeName) {
    children.push(
      new Paragraph({
        children: [
          new TextRun({
            text: formData.cvUId,
            font: FONT,
            italics: true,
            size: PT(10),
            color: "D0D0D0",
          }),
        ],
        spacing: { after: 120 },
      })
    );
  }

  if (includeName && formData.name.trim()) {
    children.push(
      new Paragraph({
        children: formatFieldRuns(formData.name.trim(), 20, 16),
        spacing: { after: 80 },
      })
    );
  }

  children.push(
    new Paragraph({
      children: formatFieldRuns(formData.position.name, 12, 9.5),
      spacing: { after: 200 },
    })
  );

  addSectionTitle(children, "PROFESSIONAL SUMMARY");
  children.push(
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      children: [
        new TextRun({
          text: formData.professionalSummary || "",
          font: FONT,
          size: PT(BODY_PT),
        }),
      ],
      spacing: { after: 400 },
    })
  );

  addSectionTitle(children, "SKILL SUMMARY");
  const skillParas: Paragraph[] = [];
  pushSkillLine(skillParas, "Backend", formData.skills.backend);
  pushSkillLine(skillParas, "Frontend", formData.skills.frontend);
  pushSkillLine(skillParas, "DevOps", formData.skills.devOps);
  pushSkillLine(skillParas, "Cloud", formData.skills.cloud);
  pushSkillLine(skillParas, "Database", formData.skills.database, ",");
  pushSkillLine(skillParas, "Tools", formData.skills.tools);
  pushSkillLine(skillParas, "Others", formData.skills.others, ",");
  children.push(...skillParas);
  children.push(new Paragraph({ spacing: { after: 400 } }));

  addSectionTitle(children, "PROFESSIONAL AFFILIATIONS");

  for (const affiliation of formData.professionalAffiliations) {
    const sortedProjects = sortProjectsByDate(affiliation.projects);
    for (let projIndex = 0; projIndex < sortedProjects.length; projIndex++) {
      const project = sortedProjects[projIndex];
      const isInternship = project.position.includes("Internship");

      if (projIndex === 0) {
        children.push(
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            children: [
              new TextRun({
                text: `${affiliation.employer}, ${affiliation.location}, ${affiliation.country}, `,
                font: FONT,
                bold: true,
                size: PT(BODY_PT),
              }),
              new TextRun({
                text: formatDate(affiliation.clientPeriod),
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
            spacing: { before: 160, after: 120 },
          })
        );
      }

      children.push(
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          children: [
            new TextRun({
              text: formatProjectPositionHeading(project.position),
              font: FONT,
              size: PT(BODY_PT),
              color: "2E74B5",
            }),
          ],
          spacing: { before: 120, after: 80 },
        })
      );

      if (!isInternship) {
        children.push(
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: "Client: ",
                font: FONT,
                bold: true,
                size: PT(BODY_PT),
              }),
              new TextRun({
                text: project.client || "",
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: "Role: ",
                font: FONT,
                bold: true,
                size: PT(BODY_PT),
              }),
              new TextRun({
                text: project.role || "",
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: "Period: ",
                font: FONT,
                bold: true,
                size: PT(BODY_PT),
              }),
              new TextRun({
                text: formatDate(project.projectPeriod),
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
          }),
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 80 },
            children: [
              new TextRun({
                text: "Project Description: ",
                font: FONT,
                bold: true,
                size: PT(BODY_PT),
              }),
              new TextRun({
                text: project.projectDescription || "",
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
          })
        );
      }

      children.push(
        new Paragraph({
          spacing: { before: 60, after: 60 },
          children: [
            new TextRun({
              text: "Responsibilities: ",
              font: FONT,
              bold: true,
              size: PT(BODY_PT),
            }),
          ],
        })
      );
      for (const responsibility of project.responsibilities) {
        if (!responsibility?.trim()) continue;
        children.push(
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            indent: { left: convertInchesToTwip(0.14) },
            spacing: { after: 40 },
            children: [
              new TextRun({
                text: `\u2022 ${responsibility.trim()}`,
                font: FONT,
                size: PT(BODY_PT),
              }),
            ],
          })
        );
      }

      const tech = (project.keyTechnologies || []).filter(Boolean).join(", ");
      children.push(
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          spacing: { before: 80, after: 160 },
          children: [
            new TextRun({
              text: "Key Technologies: ",
              font: FONT,
              bold: true,
              size: PT(BODY_PT),
            }),
            new TextRun({
              text: tech ? `${tech}.` : "",
              font: FONT,
              size: PT(BODY_PT),
            }),
          ],
        })
      );
    }
  }

  const hasCertifications = !!formData.certifications?.some((c) =>
    [c?.course, c?.courseId, c?.place, c?.date].some(
      (v) => (v ?? "").toString().trim() !== ""
    )
  );

  if (hasCertifications && formData.certifications?.length) {
    addSectionTitle(children, "PROFESSIONAL TRAINING/CERTIFICATIONS");
    for (const certification of formData.certifications) {
      children.push(
        new Paragraph({
          alignment: AlignmentType.JUSTIFIED,
          spacing: { after: 120 },
          children: [
            new TextRun({
              text: "Course: ",
              font: FONT,
              bold: true,
              size: PT(13),
            }),
            new TextRun({
              text: certification.course || "",
              font: FONT,
              size: PT(13),
            }),
          ],
        })
      );
      if (certification.place && certification.date) {
        children.push(
          new Paragraph({
            alignment: AlignmentType.JUSTIFIED,
            spacing: { after: 200 },
            children: [
              new TextRun({
                text: `${certification.place}, ${formatDate(
                  certification.date,
                  false,
                  true
                )}`,
                font: FONT,
                size: PT(13),
              }),
            ],
          })
        );
      } else {
        children.push(new Paragraph({ spacing: { after: 200 } }));
      }
    }
  }

  addSectionTitle(children, "EDUCATION");
  for (const education of formData.education || []) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        tabStops: [{ type: TabStopType.RIGHT, position: TabStopPosition.MAX }],
        spacing: { after: 200 },
        children: [
          new TextRun({
            text: `${education.degree} `,
            font: FONT,
            bold: true,
            size: PT(13),
          }),
          new TextRun({
            text: `${education.institution || ""}\t`,
            font: FONT,
            size: PT(13),
          }),
          new TextRun({
            text: formatDate(education.period, true),
            font: FONT,
            size: PT(13),
          }),
        ],
      })
    );
  }

  addSectionTitle(children, "LANGUAGES");
  children.push(
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: `${formData.languages.nativeLanguage}: `,
          font: FONT,
          bold: true,
          size: PT(13),
        }),
        new TextRun({
          text: "Native",
          font: FONT,
          size: PT(13),
        }),
      ],
    }),
    new Paragraph({
      alignment: AlignmentType.JUSTIFIED,
      spacing: { after: 60 },
      children: [
        new TextRun({
          text: "English: ",
          font: FONT,
          bold: true,
          size: PT(13),
        }),
        new TextRun({
          text: formData.languages.english || "",
          font: FONT,
          size: PT(13),
        }),
      ],
    })
  );
  for (const language of formData.languages.otherLanguages || []) {
    children.push(
      new Paragraph({
        alignment: AlignmentType.JUSTIFIED,
        spacing: { after: 60 },
        children: [
          new TextRun({
            text: `${language.name}: `,
            font: FONT,
            bold: true,
            size: PT(13),
          }),
          new TextRun({
            text: language.level || "",
            font: FONT,
            size: PT(13),
          }),
        ],
      })
    );
  }

  const doc = new Document({
    ...(calibriTtf
      ? {
          fonts: [
            {
              name: "Calibri",
              data: toFontBuffer(calibriTtf),
              characterSet: CharacterSet.ANSI,
            },
          ],
        }
      : {}),
    styles: DOC_STYLES,
    sections: [
      {
        properties: {
          page: {
            size: PAGE_SIZE_A4,
            margin: PAGE_MARGIN,
          },
        },
        children,
      },
    ],
  });

  return Packer.toBlob(doc);
}
